import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr, API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MaskedAmount } from '@/components/common/MaskedAmount';
import { Download, ExternalLink, ReceiptText } from 'lucide-react';

export default function PendingInvoicesPage() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({}); // milestone_id -> { invoice_no, deep_link }

  const load = async () => {
    try { const r = await api.get('/zoho/pending_invoices'); setRows(r.data || []); }
    catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    const e = edit[r.milestone_id] || {};
    try {
      await api.put(`/zoho/milestone_ref/${r.milestone_id}`, {
        zoho_invoice_number: e.invoice_no || null,
        zoho_deep_link: e.deep_link || null,
      });
      toast.success('Zoho reference saved'); load();
    } catch (e2) { toast.error(parseErr(e2)); }
  };

  const csvUrl = `${API_BASE}/zoho/pending_invoices/csv`;

  return (
    <AppShell breadcrumbs={[{ label: 'Pending Invoices' }]}>
      <div className="p-6 max-w-[1200px] mx-auto fade-up" data-testid="pending-invoices-page">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Pending Invoices</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">Confirmed billing triggers awaiting Zoho invoice entry.</p>
          </div>
          <a href={csvUrl} target="_blank" rel="noreferrer" data-testid="pending-invoices-csv">
            <Button variant="outline" size="sm"><Download size={13} className="mr-1" />Export CSV for Zoho</Button>
          </a>
        </div>
        <div className="card overflow-x-auto">
          <table className="tbl" data-testid="pending-invoices-table">
            <thead><tr><th>Project</th><th>Billing name</th><th>Milestone</th><th>Amount</th><th>Zoho invoice #</th><th>Deep link</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No pending invoices. Nice work.</td></tr>}
              {rows.map(r => (
                <tr key={r.milestone_id}>
                  <td>
                    <div className="font-medium">{r.project_name}</div>
                    <div className="text-[11.5px] font-mono text-[hsl(var(--foreground-muted))]">{r.project_code}</div>
                  </td>
                  <td className="text-[13px]">
                    {r.billing_name}
                    {r.zoho_customer_deep_link && <a href={r.zoho_customer_deep_link} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center text-[hsl(var(--primary))]"><ExternalLink size={11} /></a>}
                  </td>
                  <td>
                    <div className="text-[13px]">{r.milestone_name}</div>
                    <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">{r.trigger}</div>
                  </td>
                  <td><MaskedAmount amount={r.amount_inr} /></td>
                  <td><Input placeholder="INV-2025-…" value={(edit[r.milestone_id]?.invoice_no) ?? ''} onChange={e => setEdit(x => ({ ...x, [r.milestone_id]: { ...(x[r.milestone_id]||{}), invoice_no: e.target.value } }))} data-testid={`pending-invoice-no-${r.milestone_id}`} /></td>
                  <td><Input placeholder="https://books.zoho.in/…" value={(edit[r.milestone_id]?.deep_link) ?? ''} onChange={e => setEdit(x => ({ ...x, [r.milestone_id]: { ...(x[r.milestone_id]||{}), deep_link: e.target.value } }))} data-testid={`pending-invoice-link-${r.milestone_id}`} /></td>
                  <td className="text-right"><Button size="sm" onClick={() => save(r)} data-testid={`pending-invoice-save-${r.milestone_id}`}><ReceiptText size={13} className="mr-1" />Save</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
